import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import {
  HorizontalScroller,
  IosArrowLeft,
  IosArrowRight,
} from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import { useEffect, useState } from 'react';

import type { Dealer } from '@/components/browsing-history/types';
import { saveBrowsingHistory } from '@/components/browsing-history/utils';
import { PARALLEL_IMPORTERS } from '@/constants/global';

import { getPriceRange } from '../../page-content/utils';
import {
  mobileNavLinkConfigList,
  mobileUpcomingModelsNavLinkConfigList,
  navLinkConfigList,
  upcomingModelsNavLinkConfigList,
} from './configs';
import {
  LINK_KEY_PRICING,
  LINK_KEY_PROMO,
  LINK_KEY_SIMILAR,
} from './constants';
import { Item } from './item';
import styles from './styles.module.scss';
import type { Component, NavLinkConfigItem } from './types';

export const NavTabHeader: Component = ({
  carDetail,
  activeTab,
  prefix,
}) => {
  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      setIsScrollingUp(currentScrollY < lastScrollY);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isScrollingUp]);

  useEffect(() => {
    const { lowest, highest } = getPriceRange(carDetail.subModels);
    const isPastCar = !(carDetail.additionalProperties.isAvailable
      || carDetail.additionalProperties.isUpcoming);

    const priceRange = (() => {
      if (lowest === null && highest === null) {
        return isPastCar ? '-' : 'POA';
      }

      if (lowest !== null && highest !== null && lowest === highest) {
        return `$${lowest.toLocaleString('en-US')}`;
      }

      return `$${lowest?.toLocaleString('en-US')} - $${highest?.toLocaleString('en-US')}`;
    })();

    const dealers: Dealer[] = carDetail.dealerType === PARALLEL_IMPORTERS
      ? [{
        id: '0',
        name: 'Parallel Importer',
      }]
      : carDetail.authorizeDealers?.map(dealer => ({
        id: `${dealer.id}`,
        name: dealer.name,
      })) ?? [];

    saveBrowsingHistory({
      id: carDetail.id,
      carName: `${carDetail.make} ${carDetail.model}`,
      price: priceRange,
      dealers,
      image: carDetail.image.thumbnailUrl,
      slug: carDetail.slug,
    });
  }, [carDetail]);

  const getUpdatedNavLinkConfigList = () => {
    if (carDetail.additionalProperties.isUpcoming) {
      return {
        desktop: upcomingModelsNavLinkConfigList,
        mobile: mobileUpcomingModelsNavLinkConfigList,
      };
    }

    const isPastCar = !(carDetail.additionalProperties.isAvailable
      || carDetail.additionalProperties.isUpcoming);

    const disabledKeys = [LINK_KEY_PRICING, LINK_KEY_SIMILAR, LINK_KEY_PROMO];

    const updateLinkConfig = (link: NavLinkConfigItem) => ({
      ...link,
      disabled: isPastCar && disabledKeys.includes(link.key),
    });

    return {
      desktop: navLinkConfigList.map(updateLinkConfig),
      mobile: mobileNavLinkConfigList.map(updateLinkConfig),
    };
  };

  const {
    desktop: updatedNavLinkConfigList,
    mobile: updatedMobileNavLinkConfigList,
  } = getUpdatedNavLinkConfigList();

  return (
    <div className={cx(
      styles.containerRoot,
      !isDesktopMode && isScrollingUp && styles.isScrollingUp,
    )}>
      <nav className={styles.containerNav}>
        {/* due to share menu cutting off */}
        {isDesktopMode
          ? (
            <ul className={styles.containerNavbar}>
              {updatedNavLinkConfigList.map(({
                key, text, href, target, disabled, allowAppendSubModelId,
              }) => (
                <Item
                  key={key}
                  allowAppendSubModelId={allowAppendSubModelId}
                  disabled={disabled}
                  href={prefix + href}
                  isActive={activeTab === key}
                  target={target}
                  text={text} />
              ))}
            </ul>
          )
          : (
            <HorizontalScroller
              arrowLeft={<IosArrowLeft size={13} />}
              arrowRight={<IosArrowRight size={13} />}
              containerSideBlurClassName={
                styles.containerHorizontalScrollerSideBlur
              }
              scrollDistance={({ clientWidth }) => clientWidth}
              isSideBlurred>
              <ul className={styles.containerNavbar}>
                {updatedMobileNavLinkConfigList.map(({
                  key, text, href, target, disabled, allowAppendSubModelId,
                }) => (
                  <Item
                    key={key}
                    allowAppendSubModelId={allowAppendSubModelId}
                    disabled={disabled}
                    href={prefix + href}
                    isActive={activeTab === key}
                    target={target}
                    text={text} />
                ))}
              </ul>
            </HorizontalScroller>
          )}
      </nav>
    </div>
  );
};
